/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import "./instrument";

import { StrictMode, useState, Component, type ReactNode, type ErrorInfo } from "react";
import { createRoot } from "react-dom/client";
import { reactErrorHandler } from "@sentry/react";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createQueryClient } from "./queryClient";
import { trpc, createTrpcClient } from "./trpc";
import { TrpcClientProvider } from "./data/api";
import { AuthProvider } from "./state/AuthContext";
import App from "./App";
import { ErrorPage } from "@/components/layout/ErrorPage";
import { Toaster } from "@/components/ui/sonner";

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught error:", error, info);
  }
  render() {
    if (this.state.error) {
      return <ErrorPage error={this.state.error} />;
    }
    return this.props.children;
  }
}

const queryClient = createQueryClient();

function AppWithProviders() {
  const [trpcClient] = useState(() => createTrpcClient());
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <TrpcClientProvider client={trpcClient}>
          <BrowserRouter>
            <AuthProvider>
              <AppErrorBoundary>
                <App />
                <Toaster />
              </AppErrorBoundary>
            </AuthProvider>
          </BrowserRouter>
        </TrpcClientProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </trpc.Provider>
  );
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <AppWithProviders />
  </StrictMode>
);

const rootOptions = {
  onUncaughtError: reactErrorHandler(),
  onCaughtError: reactErrorHandler(),
  onRecoverableError: reactErrorHandler(),
};

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  const root = (import.meta.hot.data.root ??= createRoot(elem, rootOptions));
  root.render(app);
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem, rootOptions).render(app);
}
